import React, { useState, useEffect } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { bountyService } from '../../services/bountyService';
import {
  CheckCircleIcon,
  ClockIcon,
  TruckIcon,
  XCircleIcon,
  FunnelIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

const RewardsManagement = () => {
  const [userRewards, setUserRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [selectedRewards, setSelectedRewards] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    state: '',
    page: 1,
    limit: 10,
  });
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 450);
  const [pagination, setPagination] = useState({});

  useEffect(() => {
    loadUserRewards();
  }, [filters, debouncedSearch]);

  const loadUserRewards = async () => {
    try {
      setLoading(true);
      const response = await bountyService.getAllUserRewards({
        ...filters,
        search: debouncedSearch,
      });
      const responseData = response.data?.data || response.data || {};
      
      setUserRewards(Array.isArray(responseData.userRewards) ? responseData.userRewards : []);
      setPagination(responseData.pagination || {});
    } catch (error) {
      console.error('Error loading user rewards:', error);
      setMessage('Error loading user rewards');
      setUserRewards([]);
      setPagination({});
    } finally {
      setLoading(false);
    }
  };

  const handleStatusFilter = (status) => {
    setFilters(prev => ({
      ...prev,
      status: status === prev.status ? '' : status,
      page: 1,
    }));
  };

  const handleSelectReward = (rewardId) => {
    setSelectedRewards(prev => 
      prev.includes(rewardId) 
        ? prev.filter(id => id !== rewardId)
        : [...prev, rewardId]
    );
  };

  const handleSelectAll = () => {
    if (selectedRewards.length === userRewards.length) {
      setSelectedRewards([]);
    } else {
      setSelectedRewards(userRewards.map(reward => reward._id));
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedRewards.length === 0) {
      setMessage('Please select rewards to update');
      return;
    }

    try {
      setMessage('');
      await bountyService.updateRewardStatus(selectedRewards, status);
      setMessage(`${selectedRewards.length} rewards updated to ${status}`);
      setSelectedRewards([]);
      loadUserRewards();
    } catch (error) {
      console.error('Error updating reward status:', error);
      setMessage('Error updating reward status');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return <TruckIcon className="h-5 w-5 text-green-500" />;
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'cancelled':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Rewards Management</h1>
        <p className="text-gray-600">Manage user reward claims and delivery status</p>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-md ${
            message.includes('updated') || message.includes('successfully')
              ? 'bg-green-100 text-green-700 border border-green-200'
              : 'bg-red-100 text-red-700 border border-red-200'
          }`}
        >
          {message}
        </div>
      )}

      {/* Stats and Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
          {/* Stats */}
          <div className="flex space-x-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{pagination.total || userRewards.length}</p>
              <p className="text-sm text-gray-500">Total Claims</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">
                {userRewards.filter(r => r.status === 'pending').length}
              </p>
              <p className="text-sm text-gray-500">Pending</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {userRewards.filter(r => r.status === 'delivered').length}
              </p>
              <p className="text-sm text-gray-500">Delivered</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleStatusFilter('pending')}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                filters.status === 'pending'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => handleStatusFilter('delivered')}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                filters.status === 'delivered'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Delivered
            </button>
            <button
              onClick={() => handleStatusFilter('cancelled')}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                filters.status === 'cancelled'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Cancelled
            </button>
          </div>
        </div>
      </div>

      {/* Advanced Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setFilters((prev) => ({ ...prev, page: 1 }));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>

          {/* State Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State
            </label>
            <select
              value={filters.state}
              onChange={(e) => setFilters(prev => ({ ...prev, state: e.target.value, page: 1 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">All States</option>
              <option value="Lagos">Lagos</option>
              <option value="Abuja">Abuja</option>
              <option value="Kano">Kano</option>
              <option value="Rivers">Rivers</option>
              <option value="Ogun">Ogun</option>
              <option value="Kaduna">Kaduna</option>
              <option value="Oyo">Oyo</option>
              <option value="Enugu">Enugu</option>
              <option value="Delta">Delta</option>
              <option value="Anambra">Anambra</option>
            </select>
          </div>

          {/* Clear Filters */}
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchInput('');
                setFilters({ status: '', state: '', page: 1, limit: 10 });
              }}
              className="w-full px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedRewards.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-800">
              {selectedRewards.length} reward(s) selected
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => handleBulkStatusUpdate('delivered')}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <CheckIcon className="h-4 w-4 mr-1" />
                Mark as Delivered
              </button>
              <button
                onClick={() => handleBulkStatusUpdate('cancelled')}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
              >
                <XCircleIcon className="h-4 w-4 mr-1" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rewards Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              User Reward Claims
            </h3>
            <button
              onClick={handleSelectAll}
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              {selectedRewards.length === userRewards.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
        </div>

        {userRewards.length === 0 ? (
          <div className="text-center py-12">
            <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No reward claims</h3>
            <p className="mt-1 text-sm text-gray-500">No users have claimed rewards yet.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {userRewards.map((reward) => (
              <li key={reward._id} className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <input
                      type="checkbox"
                      checked={selectedRewards.includes(reward._id)}
                      onChange={() => handleSelectReward(reward._id)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <h4 className="text-sm font-medium text-gray-900">
                          {reward.user.firstName} {reward.user.lastName}
                        </h4>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(reward.status)}`}>
                          {getStatusIcon(reward.status)}
                          <span className="ml-1 capitalize">{reward.status}</span>
                        </span>
                      </div>
                      
                      <div className="mt-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-500">
                        <div>
                          <span className="font-medium">Email:</span> {reward.user.email}
                        </div>
                        <div>
                          <span className="font-medium">Phone:</span> {reward.user.phoneNumber}
                        </div>
                        <div>
                          <span className="font-medium">State:</span> {reward.user.state}
                        </div>
                        <div>
                          <span className="font-medium">Claimed:</span> {formatDate(reward.claimedAt)}
                        </div>
                      </div>
                      
                      {/* Bank Account Details */}
                      {reward.bankDetails && (
                        <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Bank Account Details</h5>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                            <div>
                              <span className="font-medium text-gray-600">Bank:</span>
                              <span className="ml-1 text-gray-800">{reward.bankDetails.bankName}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-600">Account Name:</span>
                              <span className="ml-1 text-gray-800">{reward.bankDetails.accountName}</span>
                            </div>
                            <div>
                              <span className="font-medium text-gray-600">Account Number:</span>
                              <span className="ml-1 text-gray-800 font-mono">{reward.bankDetails.accountNumber}</span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {!reward.bankDetails && (
                        <div className="mt-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                          <div className="flex items-center">
                            <svg className="h-4 w-4 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm text-yellow-800 font-medium">No bank account details provided</span>
                          </div>
                        </div>
                      )}
                      
                      <div className="mt-2">
                        <span className="font-medium text-gray-700">Reward: </span>
                        <span className="text-green-600 font-semibold">{reward.claimedReward.value}</span>
                        <span className="text-gray-500 ml-2">({reward.bounty.title})</span>
                      </div>
                      
                      <div className="mt-1">
                        <span className="font-medium text-gray-700">Address: </span>
                        <span className="text-gray-600">{reward.user.homeAddress}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {reward.status === 'pending' && (
                      <button
                        onClick={() => handleBulkStatusUpdate('delivered', [reward._id])}
                        className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                      >
                        <CheckIcon className="h-4 w-4 mr-1" />
                        Deliver
                      </button>
                    )}
                    {reward.status !== 'cancelled' && (
                      <button
                        onClick={() => handleBulkStatusUpdate('cancelled', [reward._id])}
                        className="inline-flex items-center px-3 py-1 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                      >
                        <XCircleIcon className="h-4 w-4 mr-1" />
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages && pagination.pages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={filters.page === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={filters.page === pagination.pages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing page <span className="font-medium">{filters.page}</span> of{' '}
                <span className="font-medium">{pagination.pages}</span> ({pagination.total} total)
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={filters.page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={filters.page === pagination.pages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardsManagement;
